import * as yup from 'yup'
import {
  aggregatorFormFields as AFF,
  aggParentFields,
  authTypes,
} from 'components/aggregatorManagement/utils'
import { HOST_REGEX, NO_SPECIAL_CHAR, NUMBER_REGEX } from 'utils/constants'
import './customYupMethods'

export const checkLastChar = (word: any) => {
  const lastLetter = word[word.length - 1]
  if (lastLetter === '/' || lastLetter === '\\') {
    return false
  }
  return true
}

const makeConditionallyRequired = (key: string, message: string) => {
  return yup
    .string()
    .trim()
    .when(key, {
      is: true,
      then: (schema) => schema.required(message),
      otherwise: (schema) => schema,
    })
}

// Aggregator FTP Configuration Schema
const ftpConfigSchema = yup.object().shape({
  [AFF.FTP_ACTIVE.key]: yup.boolean(),
  [AFF.FTP_DELETE_FILES.key]: yup.boolean(),
  [AFF.FTP_PROTOCOL.key]: yup
    .string()
    .trim()
    .required('FTP Protocol is required field'),
  [AFF.FTP_HOST.key]: yup
    .string()
    .trim()
    .when(AFF.FTP_ACTIVE.key, {
      is: true,
      then: (schema) => schema.required('FTP Host is a required field'),
      otherwise: (schema) => schema,
    })
    .matches(HOST_REGEX, {
      message: 'Please provide valid host',
      excludeEmptyString: true,
    }),
  [AFF.FTP_PORT.key]: yup
    .string()
    .trim()
    .ensure()
    .when(AFF.FTP_ACTIVE.key, {
      is: true,
      then: (schema) => schema.required('FTP Port is required field'),
      otherwise: (schema) => schema,
    })
    .matches(NUMBER_REGEX, {
      message: 'FTP Port must be positive numerical value',
      excludeEmptyString: true,
    }),
  [AFF.FTP_FILE_GENERATION_LIMIT.key]: yup
    .number()
    .required('FTP File Generation Limit is required')
    .typeError('FTP File Generation Limit must be a number')
    .min(1, 'Minimum value allowed is 1')
    .max(20, 'Maximum value allowed is 20'),
  [AFF.FTP_USERNAME.key]: makeConditionallyRequired(
    AFF.FTP_ACTIVE.key,
    'FTP Username is a required field',
  ),
  [AFF.FTP_PASSWORD.key]: makeConditionallyRequired(
    AFF.FTP_ACTIVE.key,
    'FTP Password is a required field',
  ),
  [AFF.FTP_PATH.key]: makeConditionallyRequired(
    AFF.FTP_ACTIVE.key,
    'FTP Path is a required field',
  ),
})

const validateBasedOnAuth = (type: string, message: string) => {
  return yup
    .string()
    .trim()
    .when([AFF.API_ACTIVE.key, AFF.API_AUTH_TYPE.key], (fields, schema) => {
      if (fields[0] === true && fields[1] === type) {
        return schema.required(message)
      }

      return schema
    })
}

// Auth: Bearer
const bearerAuthFields = {
  [AFF.API_AUTH_URL.key]: validateBasedOnAuth(
    authTypes.BEARER,
    'URL is a required field',
  ),
  [AFF.API_AUTH_REQ_SCHEMA.key]: validateBasedOnAuth(
    authTypes.BEARER,
    'Auth Request Schema is a required field',
  ).isValidJson('Please Provide Valid Request Schema', {
    excludeEmptyString: true,
  }),
  [AFF.API_AUTH_TOKEN_KEY.key]: validateBasedOnAuth(
    authTypes.BEARER,
    'Token Key is a required field',
  ),
}

// Auth: Basic
const basicAuthFields = {
  [AFF.API_AUTH_USERNAME.key]: validateBasedOnAuth(
    authTypes.BASIC,
    'Username is a required field',
  ),
  [AFF.API_AUTH_PASSWORD.key]: validateBasedOnAuth(
    authTypes.BASIC,
    'Password is a required field',
  ),
}

// Auth: API key Based
const apiKeyAuthFields = {
  [AFF.API_AUTH_API_KEY.key]: validateBasedOnAuth(
    authTypes.API_KEY,
    'API Key is a required field',
  ),
  [AFF.API_AUTH_API_VALUE.key]: validateBasedOnAuth(
    authTypes.API_KEY,
    'API Value is a required field',
  ),
}

// Aggregator API Configuration Schema
const apiConfigurationSchema = yup.object().shape({
  // Common
  [AFF.API_ACTIVE.key]: yup.boolean(),
  [AFF.API_URL.key]: makeConditionallyRequired(
    AFF.API_ACTIVE.key,
    'API Path is a required field',
  ),
  [AFF.API_STORES.key]: yup
    .array()
    .of(yup.string())
    .when(AFF.API_ACTIVE.key, {
      is: true,
      then: (schema) =>
        schema
          .min(1, 'Please Select at least one store.')
          .required('API stores is a required field'),
      otherwise: (schema) => schema,
    }),
  [AFF.API_AUTH_TYPE.key]: yup.string(),
  [AFF.API_ROOT_SCHEMA.key]: makeConditionallyRequired(
    AFF.API_ACTIVE.key,
    'Root Schema is a required field',
  ).isValidJson('Please Provide Valid Root Schema', {
    excludeEmptyString: true,
  }),
  [AFF.API_PRODUCT_SCHEMA.key]: makeConditionallyRequired(
    AFF.API_ACTIVE.key,
    'Product Schema is a required field',
  ).isValidJson('Please Provide Valid Product Schema', {
    excludeEmptyString: true,
  }),

  ...bearerAuthFields,
  ...basicAuthFields,
  ...apiKeyAuthFields,
})

export const createAggregatorSchema = yup.object().shape({
  // Aggregator
  [AFF.AGG_NAME.key]: yup
    .string()
    .trim()
    .required('Aggregator Name is required field')
    .matches(NO_SPECIAL_CHAR, 'Please Enter Valid Aggregator Name.'),
  [AFF.AGG_ID.key]: yup
    .string()
    .trim()
    .required('Aggregator ID is required field')
    .test('last-char-check', 'Please Enter Valid Aggregator ID.', (value) =>
      checkLastChar(value),
    )
    .matches(NO_SPECIAL_CHAR, 'Please Enter Valid Aggregator ID'),
  [AFF.AGG_IS_ACTIVE.key]: yup.boolean(),

  // FTP Configurations
  [aggParentFields.FTP]: ftpConfigSchema,

  // Aggregator API
  [aggParentFields.API]: apiConfigurationSchema,
})

export type CreateAggregatorType = yup.InferType<typeof createAggregatorSchema>
